// src/lib/snapsite-fonts.ts
// Font pairs para el sistema de personalización de SnapSite utilizando Google Fonts.

export interface FontDef {
  family: string;    // nombre real de la fuente (para font-family CSS)
  cssValue: string;  // stack completo para var(--font-heading) / var(--font-body)
}

export interface FontPair {
  key: string;
  label: string;
  description: string;
  heading: FontDef;
  body: FontDef;
  isPremium: boolean;
  previewText: string;
  googleFontsUrl?: string; // URL de importación de Google Fonts
}

export const FONT_PAIRS: Record<string, FontPair> = {
  default: {
    key: 'default',
    label: 'Sistema',
    description: 'Sin carga extra',
    heading: { family: 'Inter', cssValue: 'Inter, system-ui, sans-serif' },
    body:    { family: 'Inter', cssValue: 'Inter, system-ui, sans-serif' },
    isPremium: false,
    previewText: 'Tu nombre aquí',
  },
  modern: {
    key: 'modern',
    label: 'Moderno',
    description: 'Space Grotesk + DM Sans',
    heading: {
      family: 'Space Grotesk',
      cssValue: "'Space Grotesk', system-ui, sans-serif",
    },
    body: {
      family: 'DM Sans',
      cssValue: "'DM Sans', system-ui, sans-serif",
    },
    isPremium: false,
    previewText: 'Tu nombre aquí',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400&family=Space+Grotesk:wght@400;600&display=swap',
  },
  classic: {
    key: 'classic',
    label: 'Clásico',
    description: 'Merriweather + Source Serif 4',
    heading: {
      family: 'Merriweather',
      cssValue: "'Merriweather', Georgia, serif",
    },
    body: {
      family: 'Source Serif 4',
      cssValue: "'Source Serif 4', Georgia, serif",
    },
    isPremium: false,
    previewText: 'Tu nombre aquí',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Source+Serif+4:opsz,wght@8..60,400&display=swap',
  },
  editorial: {
    key: 'editorial',
    label: 'Editorial',
    description: 'Playfair Display + Lora',
    heading: {
      family: 'Playfair Display',
      cssValue: "'Playfair Display', Georgia, serif",
    },
    body: {
      family: 'Lora',
      cssValue: "'Lora', Georgia, serif",
    },
    isPremium: true,
    previewText: 'Tu nombre aquí',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Lora:wght@400&family=Playfair+Display:wght@400;600&display=swap',
  },
  creative: {
    key: 'creative',
    label: 'Creativo',
    description: 'Syne + Inter',
    heading: {
      family: 'Syne',
      cssValue: "'Syne', system-ui, sans-serif",
    },
    body: {
      family: 'Inter',
      cssValue: 'Inter, system-ui, sans-serif',
    },
    isPremium: true,
    previewText: 'Tu nombre aquí',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Syne:wght@400;700&display=swap',
  },
  warm: {
    key: 'warm',
    label: 'Cálido',
    description: 'DM Serif Display + Nunito',
    heading: {
      family: 'DM Serif Display',
      cssValue: "'DM Serif Display', Georgia, serif",
    },
    body: {
      family: 'Nunito',
      cssValue: "'Nunito', system-ui, sans-serif",
    },
    isPremium: true,
    previewText: 'Tu nombre aquí',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Nunito:wght@400;600&display=swap',
  },
  nerd: {
    key: 'nerd',
    label: 'Terminal',
    description: 'JetBrains Mono + Roboto Mono',
    heading: {
      family: 'JetBrains Mono',
      cssValue: "'JetBrains Mono', 'Courier New', monospace",
    },
    body: {
      family: 'Roboto Mono',
      cssValue: "'Roboto Mono', 'Courier New', monospace",
    },
    isPremium: true,
    previewText: 'Tu nombre aquí',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&family=Roboto+Mono:wght@400&display=swap',
  },
  disruptive: {
    key: 'disruptive',
    label: 'Gótico',
    description: 'Pirata One + Inter',
    heading: {
      family: 'Pirata One',
      cssValue: "'Pirata One', system-ui, sans-serif",
    },
    body: {
      family: 'Inter',
      cssValue: "Inter, system-ui, sans-serif",
    },
    isPremium: true,
    previewText: 'Tu nombre aquí',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Pirata+One&family=Inter:wght@400&display=swap',
  },
  strong: {
    key: 'strong',
    label: 'Fuerte',
    description: 'Bungee + Archivo Black',
    heading: {
      family: 'Bungee',
      cssValue: "'Bungee', system-ui, sans-serif",
    },
    body: {
      family: 'Archivo Black',
      cssValue: "'Archivo Black', system-ui, sans-serif",
    },
    isPremium: true,
    previewText: 'Tu nombre aquí',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Bungee&family=Archivo+Black&display=swap',
  },
};

export const FONT_ORDER = ['default', 'modern', 'classic', 'editorial', 'creative', 'warm', 'nerd', 'disruptive', 'strong'] as const;

export function getFontPair(key?: string | null): FontPair {
  return FONT_PAIRS[key ?? 'default'] ?? FONT_PAIRS.default;
}

/** Genera el CSS de importación para un par dado. Retorna '' para 'default'. */
export function buildFontFaceCSS(pair: FontPair): string {
  if (pair.key === 'default' || !pair.googleFontsUrl) return '';
  return `@import url('${pair.googleFontsUrl}');`;
}
